/**
 * The right-hand region inspector.
 *
 * The one rule that shapes every line here: **an unmeasured region shows no
 * numbers.** The backend sends `coverage_score: 0` and `confidence: 0` for a
 * region it could not evaluate, and rendering those as "0,00" would tell the
 * user the region scored nothing - which is a measurement, and a damning one.
 * `Bilinmiyor` gets em dashes and the reason the analysis gave.
 */

import type { CoverageRegion, ProjectRegionCoverage } from "@/api/types";
import { InspectorPanel, Metric } from "@/components/product";
import { StatusBadge } from "@/components/ui";
import {
  COVERAGE_REGION_LABELS,
  COVERAGE_STATUS_TERMS,
  METRIC_HELP,
  METRIC_LABELS,
  REGION_NEXT_ACTION,
  regionNeedsMoreCapture,
} from "@/i18n/terms";
import { Link } from "react-router-dom";
import { paths } from "@/app/paths";

function score(value: number): string {
  return value.toFixed(2).replace(".", ",");
}

export function CoverageInspector({
  region,
  data,
  projectId,
}: {
  region: CoverageRegion | null;
  data: ProjectRegionCoverage | null;
  projectId: string;
}) {
  if (!region) {
    return (
      <InspectorPanel title="Bölge">
        <p className="muted">Ayrıntıları görmek için şemadan veya listeden bir bölge seçin.</p>
      </InspectorPanel>
    );
  }

  const term = data ? COVERAGE_STATUS_TERMS[data.status] : null;
  const unknown = data?.status === "unknown";
  const nextAction = data ? REGION_NEXT_ACTION[data.status] : null;

  return (
    <InspectorPanel
      title={COVERAGE_REGION_LABELS[region]}
      badge={
        term ? (
          <StatusBadge tone={term.tone}>{term.label}</StatusBadge>
        ) : (
          <StatusBadge tone="neutral">Ölçülmedi</StatusBadge>
        )
      }
    >
      {!data ? (
        <p className="muted">Bu bölge için kayıtlı ölçüm yok.</p>
      ) : (
        <>
          {/*
            Unknown means the analysis could not evaluate this region at all.
            Its stored zeros are placeholders, not results, so they are never
            rendered as numbers.
          */}
          <Metric
            label={METRIC_LABELS.coverage}
            help={METRIC_HELP.coverage}
            value={unknown ? null : score(data.coverage_score)}
          />
          <Metric
            label={METRIC_LABELS.confidence}
            help={METRIC_HELP.confidence}
            value={unknown ? null : score(data.coverage_confidence)}
          />
          <Metric
            label="Bağımsız görüş"
            value={unknown ? null : data.independent_views}
          />
          <Metric label="Kaynak çekim" value={data.source_session_count} />

          {unknown && data.unmeasurable_reason && (
            <p className="muted small coverage-inspector__reason">
              {data.unmeasurable_reason}
            </p>
          )}

          {nextAction && <p className="coverage-inspector__action">{nextAction}</p>}

          {(data.status === "insufficient" || data.status === "partial") && (
            <>
              <p className="muted small">{regionNeedsMoreCapture(region)}</p>
              <Link className="btn btn--primary" to={paths.newCorrectionSession(projectId)}>
                Düzeltme Çekimi Ekle
              </Link>
            </>
          )}
        </>
      )}
    </InspectorPanel>
  );
}
